import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import { api } from '../lib/api'

// 배정 알림톡 #{고지내역} — sgez_be RequestService와 동일 잣대
const KAKAO_DISCLOSURE_MAX_CHARS = 780
const KAKAO_DISCLOSURE_PART1_SPLIT_NOTICE =
    '(글자수 초과로 인해 분할 발송 됩니다. 다음 알림톡도 참고해주세요)\n\n'
const KAKAO_DISCLOSURE_PART2_SPLIT_NOTICE = '(2/2 · 앞 알림톡(1/2)에 이어집니다)\n\n'
/** 1회 안내(원문) — 800자 초과 시 2회 분할 안내 */
const KAKAO_DISCLOSURE_SPLIT_USER_HINT = 800

function notify(message, type = 'info', duration = 5000) {
    try {
        if (window?.toast?.show) {
            window.toast.show(message, type, duration)
            return
        }
    } catch { }
    try { window.alert(message) } catch { }
}

function buildDisclosureTextRawFromParts(commonNote, perTargetNote, existingCustomer) {
    const common = (commonNote != null ? String(commonNote) : '').trim()
    const note = (perTargetNote != null ? String(perTargetNote) : '').trim()
    let sb = '가입설계동의완료 여부 : ' + (existingCustomer ? '예' : '아니오') + '\n\n'
    if (common.length > 0) {
        sb += common
    }
    if (note.length > 0) {
        if (sb.length > 0) sb += '\n\n'
        sb += '요청메모: ' + note
    }
    return sb
}
function _safeTrimTo(s, maxLen) {
    if (s == null) return ''
    if (maxLen <= 0) return ''
    if (s.length <= maxLen) return s
    const n = Math.max(0, maxLen - 3)
    return s.substring(0, n) + '...'
}
function kakaoDisclosureWouldLoseContentToSecondAlim(full) {
    if (full == null) return false
    if (full.length <= KAKAO_DISCLOSURE_MAX_CHARS) return false
    let firstMax = KAKAO_DISCLOSURE_MAX_CHARS - KAKAO_DISCLOSURE_PART1_SPLIT_NOTICE.length
    let secondMax = KAKAO_DISCLOSURE_MAX_CHARS - KAKAO_DISCLOSURE_PART2_SPLIT_NOTICE.length
    if (firstMax < 50) firstMax = 50
    if (secondMax < 50) secondMax = 50
    if (full.length <= firstMax) return false
    let br = full.lastIndexOf('\n', firstMax)
    if (br < 20) br = firstMax
    let p1 = full.substring(0, br).trim()
    if (p1.length === 0) p1 = _safeTrimTo(full, firstMax)
    const p2 = full.length > br ? full.substring(br).trim() : ''
    if (p2.length === 0) return false
    return p2.length > secondMax
}
function computeKakaoDisclosurePreviewForRequestNew({
    commonNote,
    checkedRequestedCoverages,
    insurerOrgIds,
    perTargetNotes,
    perTargetExisting,
    commonExisting,
}) {
    const selected = Array.isArray(checkedRequestedCoverages)
        ? checkedRequestedCoverages.map((s) => String(s || '').trim()).filter(Boolean)
        : []
    let effectiveCommon = commonNote
    if (selected.length > 0) {
        const coverageSection = [
            '',
            '[포함요청 담보 목록]',
            ...selected.map((c) => `- ${c}`),
        ].join('\n')
        const raw = (commonNote || '').trim()
        effectiveCommon = raw ? `${raw}\n${coverageSection}` : coverageSection
    } else {
        effectiveCommon = commonNote
    }
    const b1 = Math.max(50, KAKAO_DISCLOSURE_MAX_CHARS - KAKAO_DISCLOSURE_PART1_SPLIT_NOTICE.length)
    const b2 = Math.max(50, KAKAO_DISCLOSURE_MAX_CHARS - KAKAO_DISCLOSURE_PART2_SPLIT_NOTICE.length)
    const approxMax = b1 + b2
    if (!insurerOrgIds || insurerOrgIds.length === 0) {
        return {
            noTargets: true,
            worstLen: 0,
            approxMax,
            wouldReject: false,
            showTwoAlimHint: false,
            blocksCreate: false,
        }
    }
    let worst = 0
    let wouldReject = false
    for (const id of insurerOrgIds) {
        const hasOverride = Object.prototype.hasOwnProperty.call(perTargetExisting, id)
        const ex = hasOverride ? !!perTargetExisting[id] : !!commonExisting
        const tnote = perTargetNotes[id] || ''
        const full = buildDisclosureTextRawFromParts(effectiveCommon, tnote, ex)
        if (full.length > worst) worst = full.length
        if (kakaoDisclosureWouldLoseContentToSecondAlim(full)) wouldReject = true
    }
    return {
        noTargets: false,
        worstLen: worst,
        approxMax,
        wouldReject,
        showTwoAlimHint: worst > KAKAO_DISCLOSURE_SPLIT_USER_HINT,
        // 표시한 최대(2회·참고) 초과, 또는 2/2에도 잘리는 본문(=백엔드 거절)일 때
        blocksCreate: wouldReject || worst > approxMax,
    }
}

// 포함요청 담보 목록 (실손보험 관련)
const REQUESTED_COVERAGE_OPTIONS = [
  '암진단비',
  '뇌/심진단비',
  'N대',
  '수술비',
  '종수술비',
  '후유장해',
  '사망',
  '간병일당',
  '재가시설',
  '암치료비',
  '뇌심치료비',
  '입원일당',
  '배상책임',
  '운전자 3대',
]

// 새 설계의뢰 생성 페이지
// - 공통 메모 + 원수사별 개별 메모/기등록 입력을 지원
export default function RequestNewPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { user } = useAuth()

    const formatPhoneKo = React.useCallback((raw) => {
        const digits = String(raw || '').replaceAll(/[^0-9]/g, '')
        if (digits.length === 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
        if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
        return digits || ''
    }, [])

    const actorLabel = React.useMemo(() => {
        const nm = String(user?.name || '').trim()
        const phone = formatPhoneKo(user?.phoneNumber)
        const base = nm || '설계사'
        return phone ? `${base}(${phone})` : base
    }, [user?.name, user?.phoneNumber, formatPhoneKo])
    const [customerName, setCustomerName] = useState('')
    const [customerRefId, setCustomerRefId] = useState(null)
    const [title, setTitle] = useState('')
    const [commonNote, setCommonNote] = useState('')
    const [commonExisting, setCommonExisting] = useState(false)
    const [checkedRequestedCoverages, setCheckedRequestedCoverages] = useState([]) // 포함요청 담보 목록
    const [isCoverageExpanded, setIsCoverageExpanded] = useState(false) // 담보 목록 펼침/접힘 상태
    const [requestSource, setRequestSource] = useState(null) // ex) 'FIRE'
    const [fireBuildingHistoryId, setFireBuildingHistoryId] = useState(null)
    const [showLifeInsurers, setShowLifeInsurers] = useState(true)
    const [commonFiles] = useState([]) // File[] (공통 첨부)
    const [commonFilesUploading, setCommonFilesUploading] = useState(false)
    const [insurerOrgIds, setInsurerOrgIds] = useState([])
    // 원수사별 개별 메모/기등록 상태
    const [perTargetNotes, setPerTargetNotes] = useState({}) // { [orgId: string]: string }
    // 원수사별 담당 매니저(수동 오버라이드)
    // - 기본값: ''(자동 배정)
    const [perTargetManagerUserId, setPerTargetManagerUserId] = useState({}) // { [orgId: string]: string }

    // 자동 배정 미리보기(지사명/매니저명)
    const [autoAssignPreviewByCompanyId, setAutoAssignPreviewByCompanyId] = useState({}) // { [companyOrgId: string]: { gaOrgName?: string, managerName?: string } }
    const [checkingAutoAssignCompanyIds, setCheckingAutoAssignCompanyIds] = useState(new Set()) // Set<string>
    // 개별 기등록 고객: "공통값 상속 + 오버라이드" 방식
    // - 공통 YES면 기본적으로 전부 YES
    // - 개별에서 체크 해제할 때만 NO(override=false)
    const [perTargetExisting, setPerTargetExisting] = useState({}) // { [orgId: string]: boolean } (override)
    const canCreate = Boolean(user)

    const [orgOptions, setOrgOptions] = useState([])
    const [orgSearch, setOrgSearch] = useState('')
    const dq = useDebouncedValue(orgSearch, 300)
    const [counterpartOrgId, setCounterpartOrgId] = useState(null)
    const [suggestedManagerId, setSuggestedManagerId] = useState(null)
    const prefillAppliedRef = useRef(false)
    const crmFromUrlRef = useRef(false)
    const prefillSelectedInsurerCodesRef = useRef(null) // string[] | null
    const prefillAutoSelectedInsurerOrgIdsRef = useRef(new Set()) // Set<string>
    const [prefillSkippedUnassignedIds, setPrefillSkippedUnassignedIds] = useState([])

    // 보험사(회사) 마스터: orgId(회사 노드) 기반 필터용
    const [insurerMasters, setInsurerMasters] = useState([])

    // BE/환경별 응답 키 차이(type vs orgType, parentOrgId vs parentId 등)에 대비한 접근자
    const orgTypeOf = React.useCallback((o) => {
        const t = o?.type ?? o?.orgType ?? o?.org_type
        return t ? String(t).toUpperCase() : ''
    }, [])
    const parentIdOf = React.useCallback((o) => {
        const v = o?.parentOrgId ?? o?.parentId ?? o?.parent_id ?? null
        return (v === '' || v === undefined) ? null : v
    }, [])

    // URL 파라미터에서 원수사 정보 가져오기 (초기 선택용)
    useEffect(() => {
        const id = searchParams.get('counterpartOrgId')
        setCounterpartOrgId(id)
        const sm = searchParams.get('suggestedManagerId')
        setSuggestedManagerId(sm ? String(sm) : null)
        loadOrgOptions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    // HealthAnalysis(유병자 추천) → 새 설계의뢰 연결 시 프리필
    useEffect(() => {
        if (prefillAppliedRef.current) return
        const key = searchParams.get('prefillKey')
        if (!key) return
        try {
            const raw = sessionStorage.getItem(key)
            if (!raw) return
            const data = JSON.parse(raw)
            if (data?.customerName && !customerName) setCustomerName(String(data.customerName))
            if (data?.customerRefId) {
                const id = Number(data.customerRefId)
                if (Number.isFinite(id) && id > 0) {
                    setCustomerRefId(id)
                }
            }
            if (data?.title && !title) setTitle(String(data.title))
            if (data?.commonNote && !commonNote) setCommonNote(String(data.commonNote))
            // 보험사 자동 선택(HealthAnalysis → 보험사별 예외 적용 → 설계의뢰)
            if (Array.isArray(data?.selectedInsurerCodes) && data.selectedInsurerCodes.length > 0) {
                prefillSelectedInsurerCodesRef.current = data.selectedInsurerCodes
            }
            if (typeof data?.commonExistingCustomer === 'boolean') {
                setCommonExisting(data.commonExistingCustomer)
            }
            if (data?.source) {
                const src = String(data.source).toUpperCase()
                setRequestSource(src)
                if (src === 'FIRE') {
                    // 화재보험 유입: 손보를 우선 노출(생보 섹션 숨김)
                    setShowLifeInsurers(false)
                }
            }
            if (data?.fireBuildingHistoryId != null) {
                const n = Number(data.fireBuildingHistoryId)
                setFireBuildingHistoryId(Number.isFinite(n) && n > 0 ? n : null)
            }
            sessionStorage.removeItem(key)
            prefillAppliedRef.current = true
        } catch (_) {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams])

    useEffect(() => {
        if (crmFromUrlRef.current) return
        const raw = searchParams.get('customerRefId')
        if (!raw) return
        const id = Number(String(raw).trim())
        if (!Number.isFinite(id) || id <= 0) return
        crmFromUrlRef.current = true
        ;(async () => {
            try {
                const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
                const data = (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
                const nm = data?.customerName != null ? String(data.customerName).trim() : ''
                setCustomerRefId(id)
                if (nm) {
                    setCustomerName((prev) => (prev && String(prev).trim() ? prev : nm))
                    setTitle((prev) => (prev && String(prev).trim() ? prev : `${nm} 고객님 설계부탁드립니다.`))
                }
                const next = new URLSearchParams(searchParams)
                next.delete('customerRefId')
                setSearchParams(next, { replace: true })
            } catch (_) {
                crmFromUrlRef.current = false
            }
        })()
    }, [searchParams, setSearchParams])

    // prefill로 받은 insurerCode[]를 orgId[]로 변환해 자동 선택 적용
    useEffect(() => {
        const codes = prefillSelectedInsurerCodesRef.current
        if (!Array.isArray(codes) || codes.length === 0) return
        if (!Array.isArray(insurerMasters) || insurerMasters.length === 0) return

        const normalizeCode = (v) => (v || '').toString().trim().toUpperCase()
        const want = new Set(codes.map(normalizeCode).filter(Boolean))
        if (want.size === 0) return

        // 일부 환경/데이터에서 insurers.code ↔ orgName/orgId 매핑이 잘못 연결되는 케이스를 방어한다.
        // (예: SAMSUNG_FIRE가 삼성생명 orgId로 연결)
        const expectedOrgNameByCode = {
            SAMSUNG: '삼성생명',
            SAMSUNG_LIFE: '삼성생명',
            SAMSUNG_FIRE: '삼성화재',
            DB: 'DB손해보험',
            DB_FIRE: 'DB손해보험',
            HANWHA: '한화손해보험',
            HANWHA_FIRE: '한화손해보험',
            HANHWALIFE: '한화생명',
            HANWHA_LIFE: '한화생명',
            HK: '흥국화재',
            HK_FIRE: '흥국화재',
            KB_NONLIFE: 'KB손해보험',
            KB_FIRE: 'KB손해보험',
            LOTTE_NONLIFE: '롯데손해보험',
            LOTTE_FIRE: '롯데손해보험',
            MERITZ: '메리츠화재',
            MERITZ_FIRE: '메리츠화재',
            MIRAEASSET: '미래에셋생명',
            MIRAEASSET_LIFE: '미래에셋생명',
        }

        const extractCode = (m) => normalizeCode(m?.code ?? m?.insurerCode ?? m?.insurer_code ?? m?.insurerCd ?? m?.insurer_cd)
        const extractOrgId = (m) => m?.orgId ?? m?.org_id ?? null
        const extractOrgNameLike = (m) => {
            const s = (m?.orgName ?? m?.org_name ?? m?.displayName ?? m?.display_name ?? m?.name ?? '').toString().trim()
            return s
        }

        const pickOrgIdForCode = (code) => {
            const expected = expectedOrgNameByCode[code] || null

            const byCode = insurerMasters.filter((m) => extractCode(m) === code)
            for (const m of byCode) {
                const oid = extractOrgId(m)
                if (oid == null || String(oid).trim() === '') continue
                if (!expected) return String(oid)
                const orgNameLike = extractOrgNameLike(m)
                if (orgNameLike && orgNameLike.includes(expected)) return String(oid)
            }

            // fallback: 코드가 틀리게 들어왔거나, orgName 매칭이 안 되는 경우 이름으로 회사 orgId를 찾는다.
            if (expected) {
                const byName = insurerMasters.find((m) => {
                    const oid = extractOrgId(m)
                    if (oid == null || String(oid).trim() === '') return false
                    const orgNameLike = extractOrgNameLike(m)
                    return !!orgNameLike && orgNameLike.includes(expected)
                })
                if (byName) return String(extractOrgId(byName))
            }

            return null
        }

        const orgIds = []
        for (const code of Array.from(want)) {
            const oid = pickOrgIdForCode(code)
            if (oid) orgIds.push(String(oid))
        }
        if (orgIds.length === 0) return

        prefillAutoSelectedInsurerOrgIdsRef.current = new Set(orgIds.map(String))
        setPrefillSkippedUnassignedIds([])
        setInsurerOrgIds((prev) => {
            const set = new Set((prev || []).map(String))
            orgIds.forEach((x) => set.add(String(x)))
            return Array.from(set)
        })

        // 한 번 적용 후에는 ref를 비워 중복 적용 방지
        prefillSelectedInsurerCodesRef.current = []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insurerMasters])

    const loadOrgOptions = React.useCallback(async () => {
        try {
            const scope = (user?.roles?.includes(Roles.OPERATOR)) ? 'all' : undefined
            const res = await api.get('/orgs/options', { params: scope ? { scope } : undefined })
            let options = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
            // 운영자는 전체, 그 외에는 본인 소속과 보험사만
            const isOperator = user?.roles?.includes(Roles.OPERATOR)
            if (!isOperator && user?.orgId) {
                options = options.filter(o => String(o.id) === String(user.orgId) || orgTypeOf(o) === 'INSURER')
            }
            setOrgOptions(options)
        } catch (err) {
            console.error('Failed to load org options:', err)
        }
    }, [user, orgTypeOf])

    // 보험사 마스터 로딩 (회사 노드 ↔ insurerMasterId 매칭)
    useEffect(() => {
        let ignore = false
        async function loadInsurerMasters() {
            try {
                const res = await api.get('/insurers')
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                const items = Array.isArray(payload) ? payload : payload?.items || []
                if (!ignore) setInsurerMasters(Array.isArray(items) ? items : [])
            } catch {
                if (!ignore) setInsurerMasters([])
            }
        }
        loadInsurerMasters()
        return () => { ignore = true }
    }, [])

    // counterpartOrgId(지점/본부)를 회사 레벨 보험사(삼성화재/삼성생명 등)로 보정하여 기본 선택
    useEffect(() => {
        if (!counterpartOrgId || !orgOptions.length) return
        if (insurerOrgIds.length > 0) return
        const idNum = Number(counterpartOrgId)
        const byId = {}
        orgOptions.forEach(o => {
            if (o && o.id != null) byId[o.id] = o
        })
        let cur = byId[idNum]
        let company = null
        const visited = new Set()
        while (cur && !visited.has(cur.id)) {
            visited.add(cur.id)
            // 회사 레벨 보험사: 상위가 생보/손보 루트(1/2)인 INSURER
            const curParentId = parentIdOf(cur)
            if (orgTypeOf(cur) === 'INSURER' && (curParentId === 1 || curParentId === 2)) {
                company = cur
                break
            }
            cur = curParentId ? byId[curParentId] : null
        }
        const targetId = company ? company.id : idNum
        setInsurerOrgIds([String(targetId)])
    }, [counterpartOrgId, orgOptions, insurerOrgIds.length, orgTypeOf, parentIdOf])

    // 선택 정책(매핑 무관):
    // - "해당 GA에서 담당자가 배정된 원수사"만 선택 가능.
    // - 담당 후보 여부는 /counterparts?orgId={companyOrgId} 로 확인하며, 응답이 1건 이상이면 "선택 가능(전달 가능)"으로 판단한다.
    const [assignedByCompanyId, setAssignedByCompanyId] = useState({}) // { [companyOrgId: string]: boolean }
    const [checkingAssignedCompanyIds, setCheckingAssignedCompanyIds] = useState(new Set()) // Set<string>
    const [candidateManagersByCompanyId, setCandidateManagersByCompanyId] = useState({}) // { [companyOrgId: string]: any[] }

    // 원수사 담당자 탭(카드)에서 "설계 의뢰"로 진입한 경우:
    // - URL로 전달된 suggestedManagerId가 실제 후보 목록에 포함되면, 해당 회사 타겟의 수동 매니저로 자동 선택한다.
    useEffect(() => {
        if (!suggestedManagerId) return
        if (!Array.isArray(insurerOrgIds) || insurerOrgIds.length !== 1) return
        const companyId = String(insurerOrgIds[0])
        const candidates = candidateManagersByCompanyId?.[companyId]
        if (!Array.isArray(candidates) || candidates.length === 0) return

        const ok = candidates.some(m => String(m?.id) === String(suggestedManagerId))
        if (!ok) return

        setPerTargetManagerUserId(prev => {
            if (prev?.[companyId]) return prev
            return { ...(prev || {}), [companyId]: String(suggestedManagerId) }
        })
    }, [suggestedManagerId, insurerOrgIds, candidateManagersByCompanyId])

    const ensureAutoAssignPreviewForCompany = React.useCallback(async (companyOrgId) => {
        const id = String(companyOrgId)
        if (Object.prototype.hasOwnProperty.call(autoAssignPreviewByCompanyId, id)) return autoAssignPreviewByCompanyId[id]
        if (checkingAutoAssignCompanyIds.has(id)) return null
        setCheckingAutoAssignCompanyIds(prev => new Set([...prev, id]))
        try {
            const res = await api.get('/requests/auto-assign-preview', { params: { insurerOrgId: Number(id) } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const data = payload || {}
            const next = {
                gaOrgName: data.gaOrgName || null,
                managerName: data.managerName || null,
            }
            setAutoAssignPreviewByCompanyId(prev => ({ ...prev, [id]: next }))
            return next
        } catch {
            const next = { gaOrgName: null, managerName: null }
            setAutoAssignPreviewByCompanyId(prev => ({ ...prev, [id]: next }))
            return next
        } finally {
            setCheckingAutoAssignCompanyIds(prev => {
                const n = new Set(prev)
                n.delete(id)
                return n
            })
        }
    }, [autoAssignPreviewByCompanyId, checkingAutoAssignCompanyIds])

    const ensureAssignedForCompany = React.useCallback(async (companyOrgId) => {
        const id = String(companyOrgId)
        // 배정 여부는 캐시해도, 후보 목록은 아직 없을 수 있으므로(배치 선조회 케이스)
        // - assigned=false 면 즉시 false 반환
        // - assigned=true 면 후보 목록이 없으면 로딩해서 채운다
        if (Object.prototype.hasOwnProperty.call(assignedByCompanyId, id)) {
            const ok = !!assignedByCompanyId[id]
            if (!ok) return false
            if (Object.prototype.hasOwnProperty.call(candidateManagersByCompanyId, id)) return true
        }
        setCheckingAssignedCompanyIds(prev => new Set([...prev, id]))
        try {
            // 설계의뢰 화면용 후보 매니저: 사업단 + 하위 전체(형제 지사 포함) 스코프
            const res = await api.get('/requests/eligible-managers', { params: { insurerOrgId: Number(id) } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const items = Array.isArray(payload) ? payload : []
            const candidates = items.filter(it => it && it.id != null)
            setCandidateManagersByCompanyId(prev => ({ ...prev, [id]: candidates }))
            const ok = candidates.length > 0
            setAssignedByCompanyId(prev => ({ ...prev, [id]: ok }))
            return ok
        } catch {
            setCandidateManagersByCompanyId(prev => ({ ...prev, [id]: [] }))
            setAssignedByCompanyId(prev => ({ ...prev, [id]: false }))
            return false
        } finally {
            setCheckingAssignedCompanyIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [assignedByCompanyId, candidateManagersByCompanyId])

    // 회사 목록의 "배정 여부"를 한번에 조회 (과호출/버벅임 완화)
    const ensureAssignedBatch = React.useCallback(async (companyOrgIds) => {
        const ids = Array.isArray(companyOrgIds) ? companyOrgIds.map(x => String(x)).filter(Boolean) : []
        if (ids.length === 0) return
        const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
        if (missing.length === 0) return
        try {
            const res = await api.get('/requests/eligible-managers/assignment-status', { params: { orgIds: missing.join(',') } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const list = Array.isArray(payload) ? payload : []
            const next = {}
            list.forEach(it => {
                const key = it?.orgId != null ? String(it.orgId) : null
                if (!key) return
                next[key] = !!it.assigned
            })
            if (Object.keys(next).length > 0) {
                setAssignedByCompanyId(prev => ({ ...prev, ...next }))
            }
        } catch {
            // 실패 시에는 기존 동작(개별 확인)이 남아있으므로 조용히 무시
        }
    }, [assignedByCompanyId])

    const unassignedSelectedIds = useMemo(
        () => insurerOrgIds.filter((id) => assignedByCompanyId[String(id)] === false),
        [insurerOrgIds, assignedByCompanyId],
    )
    const insurerNameById = useMemo(() => {
        const map = {}
        ;(orgOptions || []).forEach((org) => {
            if (org?.id == null) return
            map[String(org.id)] = String(org?.name || '').trim() || String(org.id)
        })
        return map
    }, [orgOptions])
    const prefillSkippedUnassignedNames = useMemo(
        () => prefillSkippedUnassignedIds.map((id) => insurerNameById[String(id)] || String(id)),
        [prefillSkippedUnassignedIds, insurerNameById],
    )

    const kakaoDisclosurePreview = useMemo(
        () =>
            computeKakaoDisclosurePreviewForRequestNew({
                commonNote,
                checkedRequestedCoverages,
                insurerOrgIds,
                perTargetNotes,
                perTargetExisting,
                commonExisting,
            }),
        [commonNote, checkedRequestedCoverages, insurerOrgIds, perTargetNotes, perTargetExisting, commonExisting],
    )

    useEffect(() => {
        const pendingIds = Array.from(prefillAutoSelectedInsurerOrgIdsRef.current || []).map(String)
        if (pendingIds.length === 0) return

        const resolvedIds = pendingIds.filter((id) => Object.prototype.hasOwnProperty.call(assignedByCompanyId, String(id)))
        if (resolvedIds.length === 0) return

        const skippedIds = resolvedIds.filter((id) => assignedByCompanyId[String(id)] === false)
        if (skippedIds.length > 0) {
            setInsurerOrgIds((prev) => prev.filter((id) => !skippedIds.includes(String(id))))
            setPrefillSkippedUnassignedIds((prev) => Array.from(new Set([...(prev || []).map(String), ...skippedIds])))
        }

        prefillAutoSelectedInsurerOrgIdsRef.current = new Set(
            pendingIds.filter((id) => !resolvedIds.includes(id)),
        )
    }, [assignedByCompanyId])

    // 생보/손보 카테고리 루트 id를 동적으로 찾는다 (seed id 고정 의존 제거)
    const lifeRootId = useMemo(() => {
        const root = orgOptions.find(o => {
            if (orgTypeOf(o) !== 'INSURER') return false
            const pid = parentIdOf(o)
            if (pid != null) return false
            const name = String(o?.name || '')
            return name.includes('생명') || name.includes('생보') || name === '생명보험'
        })
        return root?.id != null ? Number(root.id) : null
    }, [orgOptions, orgTypeOf, parentIdOf])
    const nonlifeRootId = useMemo(() => {
        const root = orgOptions.find(o => {
            if (orgTypeOf(o) !== 'INSURER') return false
            const pid = parentIdOf(o)
            if (pid != null) return false
            const name = String(o?.name || '')
            return name.includes('손해') || name.includes('손보') || name.includes('화재') || name.includes('해상') || name === '손해보험'
        })
        return root?.id != null ? Number(root.id) : null
    }, [orgOptions, orgTypeOf, parentIdOf])

    // 보험사 회사 레벨만 표시: parentOrgId가 생보/손보 루트인 INSURER (삼성생명/삼성화재 등)
    const companiesFromMasters = useMemo(() => {
        const ids = new Set((insurerMasters || []).map(i => i?.orgId != null ? String(i.orgId) : null).filter(Boolean))
        return orgOptions.filter(o => orgTypeOf(o) === 'INSURER' && ids.has(String(o?.id)))
    }, [orgOptions, insurerMasters, orgTypeOf])

    // active insurers 마스터(orgId) 목록: 비어있으면(로딩 실패 등) 기존 동작을 유지하기 위한 용도
    const activeCompanyIdSet = useMemo(() => {
        const set = new Set()
        for (const m of (insurerMasters || [])) {
            const oid = m?.orgId ?? m?.org_id
            if (oid == null || String(oid).trim() === '') continue
            set.add(String(oid))
        }
        return set
    }, [insurerMasters])

    const filteredInsurers = useMemo(() => {
        const q = dq ? String(dq).toLowerCase() : ''
        const byCategory = orgOptions.filter(o => {
            if (orgTypeOf(o) !== 'INSURER') return false
            const pid = parentIdOf(o)
            if (!pid) return false
            const okParent =
                (lifeRootId != null && Number(pid) === Number(lifeRootId))
                || (nonlifeRootId != null && Number(pid) === Number(nonlifeRootId))
            if (!okParent) return false
            // 원수사 마스터(active=true) 기준으로 숨김: 마스터 목록이 존재하면 그 교집합만 노출
            if (activeCompanyIdSet.size > 0 && !activeCompanyIdSet.has(String(o?.id))) return false
            if (!q) return true
            return String(o?.name || '').toLowerCase().includes(q)
        })
        if (byCategory.length > 0) return byCategory

        // fallback: org 트리 카테고리가 누락/변형된 환경에서는 insurers 마스터의 orgId를 기준으로 회사 노드만 보여준다.
        const base = (companiesFromMasters.length > 0) ? companiesFromMasters : orgOptions
        return base.filter(o => {
            if (orgTypeOf(o) !== 'INSURER') return false
            // 마스터 목록이 존재하면 그 교집합만(=companiesFromMasters) 노출
            if (activeCompanyIdSet.size > 0 && !activeCompanyIdSet.has(String(o?.id))) return false
            if (!q) return true
            return String(o?.name || '').toLowerCase().includes(q)
        })
    }, [orgOptions, dq, orgTypeOf, parentIdOf, lifeRootId, nonlifeRootId, companiesFromMasters, activeCompanyIdSet])

    const lifeInsurers = useMemo(() => {
        if (lifeRootId != null) {
            return filteredInsurers.filter(o => Number(parentIdOf(o)) === Number(lifeRootId))
        }
        // fallback: 회사명 기준(생명/생보) 분류
        return filteredInsurers.filter(o => {
            const name = String(o?.name || '')
            return name.includes('생명') || name.includes('생보')
        })
    }, [filteredInsurers, lifeRootId, parentIdOf])

    const nonlifeInsurers = useMemo(() => {
        if (nonlifeRootId != null) {
            return filteredInsurers.filter(o => Number(parentIdOf(o)) === Number(nonlifeRootId))
        }
        // fallback: 회사명 기준(손해/손보/화재/해상) 분류
        return filteredInsurers.filter(o => {
            const name = String(o?.name || '')
            return name.includes('손해') || name.includes('손보') || name.includes('화재') || name.includes('해상')
        })
    }, [filteredInsurers, nonlifeRootId, parentIdOf])

    // 진입 시점부터 "미배정"이 보이도록: 현재 화면에 보이는 보험사(회사) 목록을 백그라운드로 배정 여부 조회
    // - 배정됨(assigned=true)은 표기를 숨기고, 미배정(false)만 표기
    useEffect(() => {
        let ignore = false
        // 목록이 아직 없으면 스킵
        if (!Array.isArray(filteredInsurers) || filteredInsurers.length === 0) return () => { ignore = true }
        ; (async () => {
            // 한 번에 너무 많은 호출을 피하기 위해 "현재 화면에 보이는 목록" 기준으로만 채운다.
            // (검색어/카테고리 변경 시 자연스럽게 추가로 채워짐)
            const ids = filteredInsurers
                .map(o => (o?.id != null ? String(o.id) : null))
                .filter(Boolean)

            // 아직 모르는 것만
            const missing = ids.filter(id => !Object.prototype.hasOwnProperty.call(assignedByCompanyId, id))
            // 최대 40개까지만 선조회 (초기 진입 과호출 방지)
            const slice = missing.slice(0, 40)

            if (ignore) return
            // 배치 조회로 네트워크/렌더링 부하 감소
            await ensureAssignedBatch(slice)
        })()
        return () => { ignore = true }
    }, [filteredInsurers, assignedByCompanyId, ensureAssignedBatch])

    // 선택된 보험사(타겟)들에 대해서만 자동배정 미리보기 로딩 (과호출 방지)
    useEffect(() => {
        let ignore = false
        if (!Array.isArray(insurerOrgIds) || insurerOrgIds.length === 0) return () => { ignore = true }
        ; (async () => {
            const slice = insurerOrgIds.slice(0, 20)
            for (const id of slice) {
                if (ignore) return
                // eslint-disable-next-line no-await-in-loop
                await ensureAutoAssignPreviewForCompany(id)
            }
        })()
        return () => { ignore = true }
    }, [insurerOrgIds, ensureAutoAssignPreviewForCompany])

    const onSubmit = async (e) => {
        e.preventDefault()
        if (insurerOrgIds.length === 0) {
            notify('원수사를 먼저 선택해 주세요. 선택한 원수사 담당자에게 의뢰 내용이 공유됩니다.', 'error')
            return
        }
        // 후보가 없는 보험사는 생성 자체가 불가능(전달/알림 불가)
        const unroutable = insurerOrgIds.filter((id) => assignedByCompanyId[String(id)] === false)
        if (unroutable.length > 0) {
            notify('담당자 후보가 없는 원수사가 포함되어 의뢰를 생성할 수 없습니다. 원수사 담당자를 생성하거나 사업단/지사 담당 매니저를 먼저 배정한 뒤 다시 시도해 주세요.', 'error')
            return
        }
        if (kakaoDisclosurePreview.blocksCreate) {
            notify('설계 메모가 현재 허용 길이를 초과해 의뢰를 생성할 수 없습니다. 공통·원수사별 메모를 줄인 뒤 현재/최대 글자 수를 다시 확인해 주세요.', 'error')
            return
        }
        try {
            if (!user?.id) {
                notify('로그인 정보를 확인할 수 없습니다. 다시 로그인한 뒤 의뢰 생성을 진행해 주세요.', 'error')
                return
            }
            const payloadTargets = insurerOrgIds.map(id => {
                const hasOverride = Object.prototype.hasOwnProperty.call(perTargetExisting, id)
                const existingCustomer = hasOverride ? !!perTargetExisting[id] : !!commonExisting
                const managerUserIdRaw = perTargetManagerUserId[id]
                const managerUserId = managerUserIdRaw ? Number(managerUserIdRaw) : null
                return { insurerOrgId: Number(id), note: (perTargetNotes[id] || ''), existingCustomer, managerUserId }
            })
            
            // 포함요청 담보 목록을 공통메모에 추가
            let finalCommonNote = commonNote
            const selectedCoverages = Array.isArray(checkedRequestedCoverages)
                ? checkedRequestedCoverages.map((s) => String(s || '').trim()).filter(Boolean)
                : []
            if (selectedCoverages.length > 0) {
                const coverageSection = [
                    '',
                    '[포함요청 담보 목록]',
                    ...selectedCoverages.map((c) => `- ${c}`),
                ].join('\n')
                finalCommonNote = finalCommonNote.trim()
                    ? `${finalCommonNote}\n${coverageSection}`
                    : coverageSection
            }
            
            const res = await api.post('/requests', {
                customerName,
                customerRefId: customerRefId || null,
                title,
                commonNote: finalCommonNote,
                commonExistingCustomer: commonExisting,
                targets: payloadTargets,
                fireBuildingHistoryId: fireBuildingHistoryId || null,
            })
            const payload = res?.data?.data ?? res?.data ?? {}
            const id = payload?.requestId ?? payload?.id ?? 0

            // 공통 첨부: 생성 직후 request-level attachment로 연결 (모든 원수사 타겟이 다운로드 가능)
            if (id && Array.isArray(commonFiles) && commonFiles.length > 0) {
                setCommonFilesUploading(true)
                let anyFailed = false
                for (const file of commonFiles) {
                    try {
                        const form = new FormData()
                        form.append('file', file)
                        const up = await api.post('/files', form, { headers: { 'Content-Type': 'multipart/form-data' } })
                        const upPayload = up?.data?.data ?? up?.data ?? {}
                        const fileId = upPayload.fileId || upPayload.id || upPayload.filename
                        if (!fileId) throw new Error('no_file_id')
                        await api.post(`/requests/${id}/attachments`, {
                            fileId,
                            filename: file.name,
                            contentType: file.type,
                            size: file.size,
                            authorUserId: user.id,
                        })
                    } catch (err) {
                        // 일부 첨부 실패는 의뢰 생성 자체를 막지 않는다.
                        anyFailed = true
                        console.error('Failed to attach common file:', err)
                    }
                }
                setCommonFilesUploading(false)
                if (anyFailed) {
                    notify('일부 첨부파일을 업로드하지 못했습니다. 의뢰는 생성되었으니, 상세 화면에서 파일 용량과 네트워크 상태를 확인한 뒤 다시 첨부해 주세요.', 'warning')
                }
            }

            // 생성 완료 안내(요청): 설계사 이름 + 전화번호 표기
            try {
                const msg = `${actorLabel} 님의 설계의뢰가 생성되었습니다. 선택한 원수사 담당자에게 의뢰 내용과 첨부파일이 공유됩니다.`
                notify(msg, 'success', 3500)
            } catch { }
            navigate(`/requests/${id}`)
        } catch (err) {
            if (err?.response?.status === 403) {
                notify('의뢰를 생성할 권한이 없습니다. 관리자에게 권한을 요청한 뒤 다시 시도해 주세요.', 'error')
            } else {
                const body = err?.response?.data
                const m = (body && typeof body === 'object' && body.message) || err?.message
                const s = m != null ? String(m).trim() : ''
                notify(s && s.length > 0 ? s : '의뢰 생성에 실패했습니다. 입력 내용과 네트워크 상태를 확인한 뒤 다시 시도해 주세요.', 'error')
            }
        }
    }

    return (
        <section>
            <h1 className="text-2xl font-bold">새 설계의뢰</h1>
            <form onSubmit={onSubmit} className="mt-6 space-y-4 w-full max-w-5xl mx-auto">
                <div>
                    <label className="block text-sm">제목</label>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-sm">고객</label>
                        <ManagedCustomerAutocomplete
                            value={customerName}
                            onValueChange={(next) => {
                                setCustomerName(next)
                                setCustomerRefId(null)
                            }}
                            selectedCustomerId={customerRefId}
                            onSelectCustomer={(customer) => {
                                const id = Number(customer?.id)
                                const name = customer?.customerName ? String(customer.customerName) : ''
                                setCustomerRefId(Number.isFinite(id) && id > 0 ? id : null)
                                if (name) {
                                    setCustomerName(name)
                                    setTitle((prev) => (prev && String(prev).trim() ? prev : `${name} 고객님 설계부탁드립니다.`))
                                }
                            }}
                            onClearCustomer={() => setCustomerRefId(null)}
                            inputClassName="mt-1 w-full rounded border px-3 py-2"
                            placeholder="고객명 입력"
                            required
                        />
                    </div>
                    <div className="flex items-center gap-2 md:justify-end">
                        <input id="cec" type="checkbox" checked={commonExisting} onChange={(e) => setCommonExisting(e.target.checked)} />
                        <label htmlFor="cec" className="text-sm">가입설계동의완료 여부(공통)</label>
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between">
                        <label className="block text-sm">포함요청 담보 목록</label>
                        <button
                            type="button"
                            onClick={() => setIsCoverageExpanded(!isCoverageExpanded)}
                            className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                        >
                            <span>{checkedRequestedCoverages.length > 0 ? `선택됨 ${checkedRequestedCoverages.length}개` : ''}</span>
                            <svg
                                className={`w-4 h-4 transition-transform ${isCoverageExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                    {isCoverageExpanded && (
                        <div className="mt-2 max-h-[180px] overflow-auto rounded-md border bg-slate-50 p-3">
                            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
                                {REQUESTED_COVERAGE_OPTIONS.map((coverage) => {
                                    const checked = Array.isArray(checkedRequestedCoverages) && checkedRequestedCoverages.includes(coverage)
                                    return (
                                        <label key={coverage} className="flex items-center gap-2 text-sm text-slate-800">
                                            <input
                                                type="checkbox"
                                                className="mt-0.5"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const on = e.target.checked
                                                    setCheckedRequestedCoverages((prev) => {
                                                        const cur = Array.isArray(prev) ? prev : []
                                                        if (on) return Array.from(new Set([...cur, coverage]))
                                                        return cur.filter((x) => x !== coverage)
                                                    })
                                                }}
                                            />
                                            <span className="text-xs">{coverage}</span>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                    <div className="mt-1 text-xs text-slate-500">
                        * 체크한 담보는 공통 메모에 자동으로 포함됩니다.
                    </div>
                </div>
                <div>
                    <div className="flex items-start justify-between gap-2">
                        <label className="block text-sm">공통 메모</label>
                        <div className="shrink-0 text-right text-slate-500">
                            <div className="text-[10px] leading-tight text-slate-500">
                                현재 글자 수 / 최대 글자 수
                            </div>
                            <div className="mt-0.5 tabular-nums text-sm font-semibold text-slate-800">
                                {kakaoDisclosurePreview.noTargets
                                    ? '—'
                                    : kakaoDisclosurePreview.worstLen}
                                <span className="font-normal text-slate-300"> / </span>
                                {kakaoDisclosurePreview.approxMax}
                            </div>
                        </div>
                    </div>
                    <textarea
                        className="mt-1 w-full rounded border px-3 py-2"
                        rows={6}
                        value={commonNote}
                        onChange={(e) => setCommonNote(e.target.value)}
                    />
                    {!kakaoDisclosurePreview.noTargets && kakaoDisclosurePreview.showTwoAlimHint && !kakaoDisclosurePreview.blocksCreate && (
                        <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                            <span className="font-medium">알림톡 2회 분할: </span>
                            설계 메모(고지)가 800자를 넘는 경우, 배정 알림톡이 2건으로 연속 발송될 수 있습니다. (1회·템플릿 기준 780자, 상단 가입문구·공통·요청·원수사별 메모를 합친 “고지 전체” 길이)
                        </div>
                    )}
                    {!kakaoDisclosurePreview.noTargets && kakaoDisclosurePreview.blocksCreate && (
                        <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-900">
                            <span className="font-medium">의뢰 생성 불가: </span>
                            설계 메모(고지)가 길어 현재 글자 수가 최대(참고)를 넘깁니다. 이 상태로는 제출·생성이 되지 않습니다. 메모를 줄인 뒤 &quot;현재 / 최대&quot;를 확인해 주세요.
                        </div>
                    )}
                    {kakaoDisclosurePreview.noTargets && (
                        <div className="mt-1 text-[11px] text-slate-500">
                            원수사를 선택하면, 위에 <span className="font-medium">현재/최대 글자 수</span>(배정 알림톡 고지 전체)가 표시됩니다.
                        </div>
                    )}
                    {checkedRequestedCoverages.length > 0 && (
                        <div className="mt-2 rounded border border-blue-200 bg-blue-50 p-2">
                            <div className="text-xs font-semibold text-blue-900 mb-1">포함요청 담보 목록 (자동 추가됨)</div>
                            <div className="text-xs text-blue-800">
                                아래 담보가 공통 메모 하단에 자동으로 추가됩니다:
                            </div>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {checkedRequestedCoverages.map((coverage) => (
                                    <span
                                        key={coverage}
                                        className="inline-flex items-center rounded border border-blue-300 bg-white px-2 py-0.5 text-xs text-blue-900"
                                    >
                                        {coverage}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {/* <div>
                    <label className="block text-sm mb-1">공통 첨부파일</label>
                    <div className="text-xs text-slate-600 mb-2">첨부한 파일은 모든 원수사 타겟이 다운로드할 수 있습니다.</div>
                    <input
                        type="file"
                        multiple
                        disabled={commonFilesUploading}
                        onChange={(e) => {
                            const files = Array.from(e.target.files || [])
                            setCommonFiles(files)
                        }}
                    />
                    {commonFilesUploading && (
                        <div className="mt-2 text-xs text-slate-600">첨부파일 업로드 중...</div>
                    )}
                    {commonFiles.length > 0 && (
                        <div className="mt-2 space-y-1 text-xs">
                            {commonFiles.map((f, idx) => (
                                <div key={`${f.name}-${idx}`} className="flex items-center justify-between border rounded px-2 py-1">
                                    <div className="text-slate-700">{f.name}</div>
                                    <button
                                        type="button"
                                        className="text-slate-500 hover:text-slate-700"
                                        onClick={() => setCommonFiles(prev => prev.filter((_, i) => i !== idx))}
                                        disabled={commonFilesUploading}
                                    >
                                        제거
                                    </button>
                                </div>
                            ))}
                        </div>
                    )} 
                </div>*/}
                <hr className="my-4" />
                <div>
                    <label className="block text-sm mb-1">원수사 조직</label>
                    {/* <div className="mb-2 text-xs text-slate-600">
                        선택 가능한 원수사: <span className="font-medium">담당자 후보(사업단/지사)가 존재하는 원수사</span> 입니다.
                    </div> */}
                    {requestSource === 'FIRE' && (
                        <div className="mb-2 rounded border bg-slate-50 p-2 text-xs text-slate-700 flex items-center justify-between gap-2">
                            <div>
                                화재보험 설계의뢰: <span className="font-semibold">손해보험사</span>를 우선 노출합니다.
                            </div>
                            {!showLifeInsurers && (
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 text-xs hover:bg-white"
                                    onClick={() => setShowLifeInsurers(true)}
                                >
                                    생명보험도 보기
                                </button>
                            )}
                        </div>
                    )}
                    {prefillSkippedUnassignedNames.length > 0 && (
                        <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                            예외질환 적용 팝업에서 선택한 보험사 중{' '}
                            <span className="font-semibold">{prefillSkippedUnassignedNames.join(', ')}</span>
                            는 미배정 보험사이기 때문에 설계의뢰 대상에서 제외되었습니다.
                        </div>
                    )}
                    {unassignedSelectedIds.length > 0 && (
                        <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                            선택된 원수사 중 <span className="font-semibold">미배정 보험사</span>가 포함되어 있습니다. 해제해주세요.
                        </div>
                    )}
                    <input
                        className="mb-2 w-full rounded border px-3 py-2 text-sm"
                        placeholder="검색: 원수사명"
                        value={orgSearch}
                        onChange={(e) => setOrgSearch(e.target.value)}
                    />
                    <div className="border rounded max-h-48 overflow-y-auto p-3">
                        {filteredInsurers.length === 0 ? (
                            <div className="text-xs text-slate-500">검색 결과가 없습니다</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                {/* 생명보험 */}
                                {showLifeInsurers && (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-semibold text-slate-700">생명보험</span>
                                        {lifeInsurers.length > 0 && (
                                            <label className="flex items-center gap-1 text-xs text-slate-600">
                                                {(() => {
                                                    const allSelected = lifeInsurers.length > 0
                                                        && lifeInsurers.every(o => insurerOrgIds.includes(String(o.id)))
                                                    return (
                                                        <>
                                                            <input
                                                                type="checkbox"
                                                                checked={allSelected}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        // 생보 전체 선택 (선택 가능 대상만)
                                                                        setInsurerOrgIds(prev => {
                                                                            const allIds = lifeInsurers.map(o => String(o.id))
                                                                            const selectable = allIds.filter(id => assignedByCompanyId[String(id)] === true)
                                                                            const next = Array.from(new Set([...prev, ...selectable]))
                                                                            const skipped = allIds.filter(id => !selectable.includes(id))
                                                                            if (skipped.length > 0) {
                                                                                // eslint-disable-next-line no-alert
                                                                                notify('담당자 미배정 또는 미확인 원수사는 전체 선택에서 제외됩니다. 개별 선택 시 담당자 후보를 다시 확인합니다.', 'info')
                                                                            }
                                                                            return next
                                                                        })
                                                                    } else {
                                                                        // 생보 전체 해제
                                                                        setInsurerOrgIds(prev => prev.filter(id =>
                                                                            !lifeInsurers.some(o => String(o.id) === String(id))
                                                                        ))
                                                                    }
                                                                }}
                                                            />
                                                            <span>전체 선택</span>
                                                        </>
                                                    )
                                                })()}
                                            </label>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        {lifeInsurers.map(org => (
                                            <label key={org.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={insurerOrgIds.includes(String(org.id))}
                                                    disabled={(() => {
                                                        const id = String(org.id)
                                                        if (checkingAssignedCompanyIds.has(id)) return true
                                                        // 담당자 미배정이면 선택 불가
                                                        return assignedByCompanyId[id] === false
                                                    })()}
                                                    onChange={(e) => {
                                                        const id = String(org.id)
                                                        if (e.target.checked) {
                                                            ; (async () => {
                                                                const ok = await ensureAssignedForCompany(id)
                                                                if (!ok) {
                                                                    // eslint-disable-next-line no-alert
                                                                    notify('선택할 수 없는 원수사입니다. 담당자가 배정되어 있지 않아 의뢰를 전달할 수 없습니다. 원수사 담당자를 먼저 생성하거나 배정해 주세요.', 'error')
                                                                    return
                                                                }
                                                                setInsurerOrgIds(prev => Array.from(new Set([...prev, id])))
                                                            })()
                                                        } else {
                                                            setInsurerOrgIds(prev => prev.filter(x => x !== id))
                                                        }
                                                    }}
                                                />
                                                <span className="flex items-center gap-1">
                                                    {org.name}
                                                    {(() => {
                                                        const id = String(org.id)
                                                        if (assignedByCompanyId[id] !== false && !checkingAssignedCompanyIds.has(id)) return null
                                                        return (
                                                            <span className="ml-1 inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">
                                                                {checkingAssignedCompanyIds.has(id) ? '확인중' : '미배정'}
                                                            </span>
                                                        )
                                                    })()}
                                                </span>
                                            </label>
                                        ))}
                                        {lifeInsurers.length === 0 && (
                                            <div className="text-xs text-slate-500">해당하는 생명보험사가 없습니다</div>
                                        )}
                                    </div>
                                </div>
                                )}

                                {/* 손해보험 */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-semibold text-slate-700">손해보험</span>
                                        {nonlifeInsurers.length > 0 && (
                                            <label className="flex items-center gap-1 text-xs text-slate-600">
                                                {(() => {
                                                    const allSelected = nonlifeInsurers.length > 0
                                                        && nonlifeInsurers.every(o => insurerOrgIds.includes(String(o.id)))
                                                    return (
                                                        <>
                                                            <input
                                                                type="checkbox"
                                                                checked={allSelected}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        // 손보 전체 선택 (선택 가능 대상만)
                                                                        setInsurerOrgIds(prev => {
                                                                            const allIds = nonlifeInsurers.map(o => String(o.id))
                                                                            const selectable = allIds.filter(id => assignedByCompanyId[String(id)] === true)
                                                                            const next = Array.from(new Set([...prev, ...selectable]))
                                                                            const skipped = allIds.filter(id => !selectable.includes(id))
                                                                            if (skipped.length > 0) {
                                                                                // eslint-disable-next-line no-alert
                                                                                notify('담당자 미배정 또는 미확인 원수사는 전체 선택에서 제외됩니다. 개별 선택 시 담당자 후보를 다시 확인합니다.', 'info')
                                                                            }
                                                                            return next
                                                                        })
                                                                    } else {
                                                                        // 손보 전체 해제
                                                                        setInsurerOrgIds(prev => prev.filter(id =>
                                                                            !nonlifeInsurers.some(o => String(o.id) === String(id))
                                                                        ))
                                                                    }
                                                                }}
                                                            />
                                                            <span>전체 선택</span>
                                                        </>
                                                    )
                                                })()}
                                            </label>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        {nonlifeInsurers.map(org => (
                                            <label key={org.id} className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={insurerOrgIds.includes(String(org.id))}
                                                    disabled={(() => {
                                                        const id = String(org.id)
                                                        if (checkingAssignedCompanyIds.has(id)) return true
                                                        return assignedByCompanyId[id] === false
                                                    })()}
                                                    onChange={(e) => {
                                                        const id = String(org.id)
                                                        if (e.target.checked) {
                                                            ; (async () => {
                                                                const ok = await ensureAssignedForCompany(id)
                                                                if (!ok) {
                                                                    // eslint-disable-next-line no-alert
                                                                    notify('선택할 수 없는 원수사입니다. 담당자가 배정되어 있지 않아 의뢰를 전달할 수 없습니다. 원수사 담당자를 먼저 생성하거나 배정해 주세요.', 'error')
                                                                    return
                                                                }
                                                                setInsurerOrgIds(prev => Array.from(new Set([...prev, id])))
                                                            })()
                                                        } else {
                                                            setInsurerOrgIds(prev => prev.filter(x => x !== id))
                                                        }
                                                    }}
                                                />
                                                <span className="flex items-center gap-1">
                                                    {org.name}
                                                    {(() => {
                                                        const id = String(org.id)
                                                        if (assignedByCompanyId[id] !== false && !checkingAssignedCompanyIds.has(id)) return null
                                                        return (
                                                            <span className="ml-1 inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800">
                                                                {checkingAssignedCompanyIds.has(id) ? '확인중' : '미배정'}
                                                            </span>
                                                        )
                                                    })()}
                                                </span>
                                            </label>
                                        ))}
                                        {nonlifeInsurers.length === 0 && (
                                            <div className="text-xs text-slate-500">해당하는 손해보험사가 없습니다</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {insurerOrgIds.length > 0 && (
                        <div className="mt-3 space-y-3">
                            {insurerOrgIds.map(id => {
                                const org = orgOptions.find(o => String(o.id) === String(id))
                                const note = perTargetNotes[id] || ''
                                const hasOverride = Object.prototype.hasOwnProperty.call(perTargetExisting, id)
                                const existing = hasOverride ? !!perTargetExisting[id] : !!commonExisting
                                const assignedVal = assignedByCompanyId[String(id)]
                                const checking = checkingAssignedCompanyIds.has(String(id))
                                return (
                                    <div key={id} className="rounded border p-3">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium text-sm flex items-center gap-2 min-w-[88px]">
                                                <span className="truncate">{org?.name || id}</span>
                                                {(checking || assignedVal === false) && (
                                                    <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800 whitespace-nowrap">
                                                        {checking ? '확인중' : '미배정'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <select
                                                    className="w-full rounded border px-3 py-2 text-sm"
                                                    value={perTargetManagerUserId[id] ?? ''}
                                                    onChange={(e) => setPerTargetManagerUserId(prev => ({ ...prev, [id]: e.target.value }))}
                                                    disabled={checking || assignedVal === false}
                                                >
                                                    {(() => {
                                                        const pv = autoAssignPreviewByCompanyId[String(id)]
                                                        const gaName = pv?.gaOrgName || '-'
                                                        const mgrName = pv?.managerName || '미배정'
                                                        return (
                                                            <option value="">
                                                                {`담당매니저(예외/대직자 선택) · 자동배정 - (${gaName}) ${mgrName}`}
                                                            </option>
                                                        )
                                                    })()}
                                                {(candidateManagersByCompanyId[String(id)] || []).map((m) => {
                                                    const name = m?.name || '-'
                                                    const position = m?.position || '담당자'
                                                    const gaNames = Array.isArray(m?.gaBranchOrgNames) ? m.gaBranchOrgNames : []
                                                    const gaName = (gaNames.length > 0 ? gaNames.join(', ') : (m?.gaBranchOrgName || null))
                                                    const gaLabel = gaName ? `${gaName} 담당` : '담당지사 미지정'
                                                    const label = `${name}(${position}, ${gaLabel})`
                                                    return (
                                                        <option key={String(m.id)} value={String(m.id)}>
                                                            {label}
                                                        </option>
                                                    )
                                                })}
                                                </select>
                                            </div>
                                            <button
                                                type="button"
                                                className="text-xs text-slate-500 hover:text-slate-700 whitespace-nowrap"
                                                onClick={() => setInsurerOrgIds(prev => prev.filter(x => x !== id))}
                                            >
                                                제거
                                            </button>
                                        </div>
                                        {assignedVal === false && (
                                            <div className="mt-2 text-xs text-amber-800">
                                                {'정책상 선택할 수 없습니다. (담당자 후보 없음)\n원수사 담당자 생성/배정을 먼저 진행해주세요.'}
                                            </div>
                                        )}
                                        <div className="mt-2 flex items-center gap-3">
                                            <input
                                                className="flex-1 min-w-0 rounded border px-3 py-2 text-sm"
                                                placeholder="개별메모 입력"
                                                value={note}
                                                onChange={(e) => setPerTargetNotes(prev => ({ ...prev, [id]: e.target.value }))}
                                            />
                                            <label className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={existing}
                                                    onChange={(e) => setPerTargetExisting(prev => ({ ...prev, [id]: e.target.checked }))}
                                                />
                                                기등록 고객 여부
                                            </label>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
                {/* 개별 메모/기등록은 원수사별 블록에서 입력 */}

                <div className="rounded border border-sky-200 bg-sky-50/70 px-3 py-2 text-xs leading-5 text-sky-900">
                    생성하면 선택한 원수사 담당자에게 의뢰가 공유됩니다. 공통 메모와 첨부파일은 의뢰 처리 목적으로만 사용되며, 생성 후 상세 화면에서 코멘트와 첨부를 추가할 수 있습니다.
                </div>
                <div className="pt-2">
                    <button
                        className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-50"
                        type="submit"
                        disabled={commonFilesUploading || kakaoDisclosurePreview.blocksCreate}
                    >
                        생성
                    </button>
                </div>
            </form>
        </section>
    )
}


